/**
 * Meta Pixel (Facebook Pixel) Integration
 * Gestisce il tracciamento Facebook Pixel rispettando il consenso dei cookie
 */

interface FbqFunction {
  (action: 'init', pixelId: string): void;
  (action: 'track', event: string, params?: Record<string, unknown>): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  push?: unknown;
  loaded?: boolean;
  version?: string;
}

declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
  }
}

const PIXEL_ID = '2301699206999371';

/**
 * Verifica se il consenso ai cookie di tracciamento è stato dato
 */
function hasTrackingConsent(): boolean {
  const consent = sessionStorage.getItem('cookieConsent');
  return consent === 'accepted';
}

/**
 * Inizializza il Meta Pixel
 */
export function initMetaPixel(): void {
  if (!hasTrackingConsent()) {
    return;
  }

  // Verifica se fbq è già stato caricato
  if (window.fbq) {
    return;
  }

  // Carica lo script del Meta Pixel usando l'approccio ufficiale di Facebook
  // Implementazione conforme al codice fornito da Meta
  const w = window;
  const d = document;
  const s = 'script';
  const id = 'facebook-jssdk';
  
  if (w.fbq) return;
  
  const fbq = function (...args: unknown[]) {
    const fbqInstance = w.fbq;
    if (!fbqInstance) return;
    if (fbqInstance.callMethod) {
      fbqInstance.callMethod(...args);
    } else if (fbqInstance.queue) {
      fbqInstance.queue.push(args);
    }
  } as FbqFunction;
  
  w.fbq = fbq;
  if (!w._fbq) w._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  
  const js = d.createElement(s);
  js.id = id;
  js.async = true;
  js.src = 'https://connect.facebook.net/en_US/fbevents.js';
  
  const fjs = d.getElementsByTagName(s)[0];
  if (fjs && fjs.parentNode) {
    fjs.parentNode.insertBefore(js, fjs);
  }

  // Inizializza il pixel
  window.fbq?.('init', PIXEL_ID);
  window.fbq?.('track', 'PageView');
}

/**
 * Traccia un evento personalizzato
 */
export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
  if (!hasTrackingConsent() || !window.fbq) {
    return;
  }

  window.fbq('track', eventName, params);
}

/**
 * Traccia un click su un bottone
 */
export function trackButtonClick(buttonName: string, additionalParams?: Record<string, unknown>): void {
  trackEvent('ButtonClick', {
    button_name: buttonName,
    ...additionalParams,
  });
}

/**
 * Traccia una page view
 */
export function trackPageView(): void {
  if (!hasTrackingConsent() || !window.fbq) {
    return;
  }

  window.fbq('track', 'PageView');
}

/**
 * Traccia una prenotazione
 */
export function trackReservation(params?: Record<string, unknown>): void {
  trackEvent('Reservation', params);
}

/**
 * Traccia una chiamata telefonica
 */
export function trackPhoneCall(): void {
  trackEvent('Contact', {
    method: 'phone',
  });
}

/**
 * Traccia la visualizzazione del menu
 */
export function trackMenuView(menuType?: string): void {
  trackEvent('ViewContent', {
    content_type: 'menu',
    content_name: menuType || 'menu',
  });
}

